import React, { useState } from 'react';
import {
  AlertTriangle, Settings, Bell, Package, ShoppingCart,
  Archive, HardHat, Wrench, ClipboardList, Truck
} from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import StockWarningSettings from './StockWarningSettings';
import ActiveStockWarnings from './ActiveStockWarnings';
import PurchaseWarningSettings from './PurchaseWarningSettings';
import ActivePurchaseWarnings from './ActivePurchaseWarnings';
import InactiveWarningSettings from './InactiveWarningSettings';
import ActiveInactiveWarnings from './ActiveInactiveWarnings';
import SubcontractorWarningSettings from './SubcontractorWarningSettings';
import ActiveSubcontractorWarnings from './ActiveSubcontractorWarnings';
import ServiceWarningSettings from './ServiceWarningSettings';
import ActiveServiceWarnings from './ActiveServiceWarnings';
import DemirbasWarningSettings from './DemirbasWarningSettings';
import ActiveDemirbasWarnings from './ActiveDemirbasWarnings';
import OrderTrackingWarningSettings from './OrderTrackingWarningSettings';
import ActiveOrderTrackingWarnings from './ActiveOrderTrackingWarnings';

// Uyarı kategorileri - her biri "Uyarılar" altında bir alt-sekme
const WARNING_CATEGORIES = [
  { key: 'stok', label: 'Stok Uyarıları', icon: Package },
  { key: 'alim', label: 'Alım Uyarıları', icon: ShoppingCart },
  { key: 'hareketsiz-stok', label: 'Hareketsiz Stok Uyarıları', icon: Archive },
  { key: 'taseron-hakedis', label: 'Taşeron Hak Ediş Uyarıları', icon: HardHat },
  { key: 'hizmet-hakedis', label: 'Alınan Hizmetler Hak Ediş Uyarıları', icon: Wrench },
  { key: 'demirbas', label: 'Demirbaş Uyarıları', icon: ClipboardList },
  { key: 'siparis-takip', label: 'Sipariş Takip Uyarıları', icon: Truck },
];

const PurchasingWarnings = ({ readOnly = false }) => {
  const [activeTab, setActiveTab] = useState('uyarilar');
  const [activeCategory, setActiveCategory] = useState(WARNING_CATEGORIES[0].key);
  const [activeSettingsCategory, setActiveSettingsCategory] = useState(WARNING_CATEGORIES[0].key);

  return (
    <div className="space-y-6" data-testid="purchasing-warnings-page">
      {/* Başlık */}
      <div className="flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-amber-100">
          <AlertTriangle className="h-6 w-6 text-amber-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Uyarılar</h1>
          <p className="text-sm text-gray-500">Satın alma uyarıları ve uyarı ayarlamaları</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="uyarilar" data-testid="tab-uyarilar" className="flex items-center gap-2">
            <Bell className="h-4 w-4" />
            Uyarılar
          </TabsTrigger>
          <TabsTrigger value="ayarlamalar" data-testid="tab-uyari-ayarlamalari" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Uyarı Ayarlamaları
          </TabsTrigger>
        </TabsList>

        {/* Uyarılar Sekmesi - 7 kategori alt-sekmesi */}
        <TabsContent value="uyarilar" className="mt-6" data-testid="content-uyarilar">
          <Tabs value={activeCategory} onValueChange={setActiveCategory} className="w-full">
            <TabsList className="flex w-full flex-wrap justify-start gap-2 bg-transparent p-0 h-auto">
              {WARNING_CATEGORIES.map((cat) => {
                const Icon = cat.icon;
                return (
                  <TabsTrigger
                    key={cat.key}
                    value={cat.key}
                    data-testid={`tab-warning-${cat.key}`}
                    className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm data-[state=active]:border-amber-300 data-[state=active]:bg-amber-100 data-[state=active]:text-amber-700"
                  >
                    <Icon className="h-4 w-4" />
                    {cat.label}
                  </TabsTrigger>
                );
              })}
            </TabsList>

            {WARNING_CATEGORIES.map((cat) => {
              const Icon = cat.icon;
              
              if (cat.key === 'stok') {
                return (
                  <TabsContent key={cat.key} value={cat.key} className="mt-6" data-testid={`content-warning-${cat.key}`}>
                    <ActiveStockWarnings />
                  </TabsContent>
                );
              }
              
              if (cat.key === 'alim') {
                return (
                  <TabsContent key={cat.key} value={cat.key} className="mt-6" data-testid={`content-warning-${cat.key}`}>
                    <ActivePurchaseWarnings />
                  </TabsContent>
                );
              }

              if (cat.key === 'hareketsiz-stok') {
                return (
                  <TabsContent key={cat.key} value={cat.key} className="mt-6" data-testid={`content-warning-${cat.key}`}>
                    <ActiveInactiveWarnings />
                  </TabsContent>
                );
              }
              
              if (cat.key === 'taseron-hakedis') {
                return (
                  <TabsContent key={cat.key} value={cat.key} className="mt-6" data-testid={`content-warning-${cat.key}`}>
                    <ActiveSubcontractorWarnings />
                  </TabsContent>
                );
              }

              if (cat.key === 'hizmet-hakedis') {
                return (
                  <TabsContent key={cat.key} value={cat.key} className="mt-6" data-testid={`content-warning-${cat.key}`}>
                    <ActiveServiceWarnings />
                  </TabsContent>
                );
              }

              if (cat.key === 'demirbas') {
                return (
                  <TabsContent key={cat.key} value={cat.key} className="mt-6" data-testid={`content-warning-${cat.key}`}>
                    <ActiveDemirbasWarnings />
                  </TabsContent>
                );
              }

              if (cat.key === 'siparis-takip') {
                return (
                  <TabsContent key={cat.key} value={cat.key} className="mt-6" data-testid={`content-warning-${cat.key}`}>
                    <ActiveOrderTrackingWarnings />
                  </TabsContent>
                );
              }

              return (
                <TabsContent key={cat.key} value={cat.key} className="mt-6" data-testid={`content-warning-${cat.key}`}>
                  <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 bg-white py-16 text-center">
                    <Icon className="mb-3 h-10 w-10 text-gray-300" />
                    <p className="text-base font-medium text-gray-700">{cat.label}</p>
                    <p className="mt-1 text-sm text-gray-400">
                      Bu kategori için uyarı ayarlamaları tanımlandıkça, eşleşen uyarılar burada listelenecek.
                    </p>
                  </div>
                </TabsContent>
              );
            })}
          </Tabs>
        </TabsContent>

        {/* Uyarı Ayarlamaları Sekmesi - 7 kategori alt-sekmesi */}
        <TabsContent value="ayarlamalar" className="mt-6" data-testid="content-uyari-ayarlamalari">
          <Tabs value={activeSettingsCategory} onValueChange={setActiveSettingsCategory} className="w-full">
            <TabsList className="flex w-full flex-wrap justify-start gap-2 bg-transparent p-0 h-auto">
              {WARNING_CATEGORIES.map((cat) => {
                const Icon = cat.icon;
                return (
                  <TabsTrigger
                    key={cat.key}
                    value={cat.key}
                    data-testid={`tab-settings-${cat.key}`}
                    className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm data-[state=active]:border-amber-300 data-[state=active]:bg-amber-100 data-[state=active]:text-amber-700"
                  >
                    <Icon className="h-4 w-4" />
                    {cat.label}
                  </TabsTrigger>
                );
              })}
            </TabsList>

            {/* Stok Uyarı Ayarları - tam form */}
            <TabsContent value="stok" className="mt-6" data-testid="content-settings-stok">
              <StockWarningSettings readOnly={readOnly} />
            </TabsContent>
            
            <TabsContent value="alim" className="mt-6" data-testid="content-settings-alim">
              <PurchaseWarningSettings readOnly={readOnly} />
            </TabsContent>
            
            <TabsContent value="hareketsiz-stok" className="mt-6" data-testid="content-settings-hareketsiz-stok">
              <InactiveWarningSettings readOnly={readOnly} />
            </TabsContent>
            
            <TabsContent value="taseron-hakedis" className="mt-6" data-testid="content-settings-taseron-hakedis">
              <SubcontractorWarningSettings readOnly={readOnly} />
            </TabsContent>
            
            <TabsContent value="hizmet-hakedis" className="mt-6" data-testid="content-settings-hizmet-hakedis">
              <ServiceWarningSettings readOnly={readOnly} />
            </TabsContent>
            
            <TabsContent value="demirbas" className="mt-6" data-testid="content-settings-demirbas">
              <DemirbasWarningSettings readOnly={readOnly} />
            </TabsContent>
            
            <TabsContent value="siparis-takip" className="mt-6" data-testid="content-settings-siparis-takip">
              <OrderTrackingWarningSettings readOnly={readOnly} />
            </TabsContent>

            {/* Diğer kategoriler - şimdilik placeholder */}
            {WARNING_CATEGORIES.filter((c) => c.key !== 'stok' && c.key !== 'alim' && c.key !== 'hareketsiz-stok' && c.key !== 'taseron-hakedis' && c.key !== 'hizmet-hakedis' && c.key !== 'demirbas' && c.key !== 'siparis-takip').map((cat) => {
              const Icon = cat.icon;
              return (
                <TabsContent key={cat.key} value={cat.key} className="mt-6" data-testid={`content-settings-${cat.key}`}>
                  <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 bg-white py-16 text-center">
                    <Icon className="mb-3 h-10 w-10 text-gray-300" />
                    <p className="text-base font-medium text-gray-700">{cat.label} — Ayarlar</p>
                    <p className="mt-1 text-sm text-gray-400">Bu kategori için uyarı ayarları yakında tanımlanacak.</p>
                  </div>
                </TabsContent>
              );
            })}
          </Tabs>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PurchasingWarnings;
